import React, { useCallback, useEffect, useRef } from "react";

import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import styles from "./ResizeHandle.module.scss";
import parseValue from "./util";

interface Props {
  container: HTMLElement;
  max?: number | string;
  maxPercent?: number;
  min?: number | string;
  onResize?: (width: number) => void;
}

const ResizeHandle: React.FC<Props> = ({ container, max, maxPercent, min, onResize }) => {
  const [isResizing, startResizing, stopResizing] = useBoolean(false);
  const handle = useRef<HTMLDivElement | null>(null);

  const limit = useCallback(
    (width: number) => {
      let result = width;

      // If given maxPercent - apply size constraints relative to actual screen width
      if (maxPercent !== undefined && maxPercent > 0) {
        result = Math.min(result, (container.offsetWidth * maxPercent) / 100);
      }

      if (max !== undefined && max !== null) {
        const maxParsed = parseValue(max);
        if (maxParsed.unit === "%") {
          result = Math.min(result, (maxParsed.value / 100) * container.offsetWidth);
        } else {
          result = Math.min(result, maxParsed.value);
        }
      }

      if (min !== undefined && min !== null) {
        const minParsed = parseValue(min);
        if (minParsed.unit === "%") {
          result = Math.max(result, (minParsed.value / 100) * container.offsetWidth);
        } else {
          result = Math.max(result, minParsed.value);
        }
      }

      return Math.round(result);
    },
    [container, max, maxPercent, min]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (container && isResizing && handle?.current) {
        const calculatedWidth = container.offsetWidth - (e.clientX - container.offsetLeft);

        const offsetRight = limit(calculatedWidth);

        handle.current.style.right = `${offsetRight}px`;
        handle.current.style.display = "block";
      }
    },
    [container, isResizing, limit]
  );

  const handleMouseDown = useCallback(() => {
    if (container && !isResizing) {
      startResizing();
    }
  }, [container, isResizing, startResizing]);

  const handleMouseUp = useCallback(
    (e) => {
      if (container && isResizing && handle?.current) {
        const calculatedWidth = container.offsetWidth - (e.clientX - container.offsetLeft);
        const offsetRight = limit(calculatedWidth);

        stopResizing();

        onResize?.(offsetRight);
      }
    },
    [container, isResizing, limit, onResize, stopResizing]
  );

  useEffect(() => {
    if (container && isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      container.classList.add(styles.overflow);

      const overlay = document.createElement("div");
      overlay.className = styles.overlay;
      container.appendChild(overlay);

      const dragger = document.createElement("div");
      dragger.className = styles.handle;
      handle.current = dragger;
      container.appendChild(dragger);

      return () => {
        container.removeChild(dragger);
        container.removeChild(overlay);
        container.classList.remove(styles.overflow);

        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [container, handleMouseMove, handleMouseUp, isResizing, stopResizing]);

  return <div className={styles.container} onMouseDown={handleMouseDown}></div>;
};

export default ResizeHandle;
